<template>
  <nv-page header="Classify Domain" data-cy="domain-classify">
    <template #toolbar>
      <v-spacer />
      <v-chip v-if="items.length" label outlined>
        Lock expires at {{ items[0].lock_expires_at | time('short') }}
      </v-chip>
      <v-spacer />
      <v-btn
        v-if="items.length && canViewMav"
        :class="['use-mav-btn mr-2', { 'use-mav-btn--active': mavBatch.hasInFlight }]"
        dark
        @click="mavBatch.show = true"
      >
        <v-progress-circular
          v-if="mavBatch.hasInFlight"
          indeterminate
          size="14"
          width="2"
          color="white"
          class="mr-2"
        />
        <v-icon v-else class="mr-1">mdi-robot</v-icon>
        <span>{{ mavBatch.hasInFlight ? 'Batch Running' : 'Use MAV' }}</span>
      </v-btn>
      <v-btn
        v-if="canViewDebug"
        text
        class="mr-2"
        @click="$router.push({ name: 'owl.domain.sessions.list' })"
      >
        <v-icon small class="mr-1">mdi-history</v-icon>
        <span>Sessions</span>
      </v-btn>
      <v-btn
        v-if="$auth.hasPermissions('owl.marketview')"
        color="secondary"
        @click="createForm.show = true"
      >
        <v-icon class="mr-1">mdi-plus</v-icon>
        <span>Create Channel</span>
      </v-btn>
      <nv-btn
        v-if="$auth.hasPermissions('ant.discovery')"
        type="unlock"
        data-cy="classify-unlock-btn"
        @click="unlock()"
      />
    </template>

    <template #filters>
      <nv-table-filters ref="applied-filters" v-model="tableFilters" no-clear />
    </template>

    <v-data-table
      :items="items"
      :loading="loading"
      :options.sync="table"
      :server-items-length="totalItems"
      :footer-props="footerProps"
      :items-per-page.sync="table.itemsPerPage"
      :headers-length="5"
      hide-default-header
    >
      <template #header>
        <thead class="v-data-table-header">
          <tr>
            <th>Domain</th>
            <th colspan="3">
              <v-row>
                <v-col cols="5">Tree ID</v-col>
              </v-row>
            </th>
          </tr>
        </thead>
      </template>
      <template #item="{ item }">
        <classify-row :item="item" />
      </template>
    </v-data-table>
    <brand-channel-form v-model="createForm.show" channel cls />
    <mav-stream-window v-if="canViewDebug" />
    <mav-batch-dialog
      v-model="mavBatch.show"
      :items="items"
      :has-known-in-flight="mavBatch.hasInFlight"
      @apply-prediction="onBatchPrediction"
      @batch-state="onBatchStateChange"
    />
  </nv-page>
</template>

<script>
import ClassifyList from '@/components/cls/ClassifyList';
import ClassifyRow from '@/components/cls/domain/ClassifyRow';
import BrandChannelForm from '@/components/mv/BrandChannelForm';
import MavStreamWindow from '@/components/cls/domain/MavStreamWindow';
import MavBatchDialog from '@/components/cls/domain/MavBatchDialog';
import { getConfigMap } from '@/helpers/filters';
import { MavStreamBus } from '@/helpers/mavStreamBus';
import { listBatches } from '@/api/mav-batch';

export default {
  components: { BrandChannelForm, ClassifyRow, MavStreamWindow, MavBatchDialog },
  extends: ClassifyList('domain'),
  data: () => ({
    table: {
      sortBy: ['rank'],
      sortDesc: [true],
    },
    mavBatch: {
      show: false,
      hasInFlight: false,
    },
    pendingOpenMav: false,
  }),
  computed: {
    canViewMav() {
      const config = getConfigMap.call(this);
      const mavConfig = config['mavFeature_owl_ui'];
      return mavConfig?.enabled?.v1 || mavConfig?.enabled?.v2;
    },
    canViewDebug() {
      const config = getConfigMap.call(this);
      return config['mavFeature_owl_ui']?.enabled?.debug === true;
    },
  },
  watch: {
    items(newItems) {
      // When items are loaded and we have a pending MAV open request
      if (this.pendingOpenMav && newItems && newItems.length > 0) {
        this.pendingOpenMav = false;
        this.mavBatch.show = true;
      }
    },
  },
  created() {
    this.setTableSort();
  },
  mounted() {
    this.checkInFlightBatches();
    this.checkAutoOpenMav();
  },
  methods: {
    setTableSort() {
      if (localStorage.getItem('domain_sort_by')) {
        this.table.sortBy = [localStorage.getItem('domain_sort_by')];
      }
      if (localStorage.getItem('domain_sort_desc')) {
        this.table.sortDesc = [localStorage.getItem('domain_sort_desc')];
      }
    },
    checkAutoOpenMav() {
      // Check if we should auto-open the MAV dialog (from discovery page USE MAV)
      if (this.$route.query.openMav === 'true' && this.canViewMav) {
        // Clean up the query param to prevent re-opening on refresh
        this.$router.replace({
          name: 'owl.domain.classify.list',
          query: {},
        });

        // If items are already loaded, open immediately
        if (this.items && this.items.length > 0) {
          this.mavBatch.show = true;
        } else {
          // Otherwise, set flag to open when items are loaded
          this.pendingOpenMav = true;
        }
      }
    },
    async checkInFlightBatches() {
      if (!this.canViewMav) return;
      try {
        const batches = await listBatches();
        if (!Array.isArray(batches)) return;
        const inFlight = batches.find(b => b.status === 'queued' || b.status === 'running');
        this.mavBatch.hasInFlight = !!inFlight;
        if (inFlight) {
          // Auto-open the dialog — it will detect and resume the batch.
          this.mavBatch.show = true;
        }
      } catch (err) {
        // Non-fatal; user can still open the dialog manually.
      }
    },
    onBatchStateChange(state) {
      // Emitted by MavBatchDialog when batch status changes.
      const terminal = ['completed', 'failed', 'canceled', 'expired'];
      this.mavBatch.hasInFlight = !terminal.includes(state);
    },
    onBatchPrediction({ domain, prediction }) {
      // Find matching ClassifyRow by domain and apply the prediction.
      MavStreamBus.$emit('mav-batch-apply', { domain, prediction });
    },
  },
};
</script>

<style scoped>
.use-mav-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3) !important;
}
.use-mav-btn--active {
  animation: mav-pulse 2s ease-in-out infinite;
}
@keyframes mav-pulse {
  0%,
  100% {
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  }
  50% {
    box-shadow: 0 2px 16px rgba(102, 126, 234, 0.7);
  }
}
</style>
